// The Reports page's drill-down (B10 S15; plan D7; DC-210, DC-216; CR-051 "security-trimmed drill-down"; CR-060
// "Filled/Cancelled archive immediately and remain searchable/reportable/auditable/reopenable."). It draws exactly the
// rows `report.drill` returned for the reader — requisition rows, candidate rows where the reader may open the
// application, and identifier-free requisition counts for the rest — and never asks for, keeps or derives another.
// A restricted column (*Recommended hiring range*) is drawn only when some row carries it: the server leaves the key
// out where the field policy hides it, so the Hiring Manager's table has no such column at all. Each duration reads
// Active Time as the number with Calendar Time beside it ("SLA uses Active Time; Calendar Time remains visible.").

import type { ReactNode } from "react";
import {
  COMMON_LABELS, G6_COLUMN_LABELS, Health, LEVEL_DISPLAY, METRIC_LABELS, NAV_ITEM_LABELS, Pill, REQUISITION_STATUS, ROLE_LABELS,
  SLA_LABELS, Table, Td, TextLink, Th, Tr, formatDate, formatDateTime, type HealthBand, type Level,
} from "@/mc";
import type { AggregateRow, ReportDrillOutput } from "@/api/tools";
import { REPORT_DIMENSION_LABELS, REPORT_MEASURE_WORDS, REPORT_PAGE_LABELS } from "./labels";
import { rptHs } from "./spec";

// ---- the drill rows' contract (backend/app/reporting/tools/report.py; the generated client types them unknown) -------

export interface PersonRef {
  id: string;
  name?: string | null | undefined;
}

export interface FigureCell {
  status: string;
  calendar?: number | null | undefined;
  active?: number | null | undefined;
  hold?: number | null | undefined;
}

export interface HoldRow {
  hold_id: string;
  placed_at: string;
  resumed_at?: string | null | undefined;
  status: "open" | "ended";
  reason: string;
  reason_label?: string | null | undefined;
  reason_config_version_id?: string | null | undefined;
  days: number;
}

export interface HealthCell {
  status: "available" | "not_yet_available";
  score?: number | null | undefined;
  light?: HealthBand | null | undefined;
  drivers?: readonly { key: string; route?: string | null | undefined }[] | undefined;
}

export interface ReadinessCell {
  percent?: number | null | undefined;
  measured: number;
  total: number;
  posting_state: string;
}

export interface RecommendedRangeCell {
  min?: string | number | null | undefined;
  max?: string | number | null | undefined;
  currency: string;
}

export interface RequisitionRowOut {
  requisition_id: string;
  title: string;
  level: string;
  department: string;
  function: string;
  brands: readonly string[];
  locations: readonly string[];
  status: string;
  archived: boolean;
  confidential: boolean;
  recruiter?: PersonRef | null | undefined;
  hiring_manager: PersonRef;
  days_open: FigureCell;
  holds: readonly HoldRow[];
  health: HealthCell;
  readiness?: ReadinessCell | null | undefined;
  config_versions: Readonly<Record<string, unknown>>;
  route: string;
  opens: "requisition.open" | "archive.open";
  /** Absent, never null, where the field policy hides it on this requisition. */
  recommended_range?: RecommendedRangeCell | undefined;
}

export interface StageAge {
  since: string;
  calendar: number;
  active: number;
  hold: number;
}

export interface ApplicationRowOut {
  application_id: string;
  candidate_id: string;
  requisition_id: string;
  requisition_title: string;
  stage?: string | null | undefined;
  column?: string | null | undefined;
  held: boolean;
  in_stage?: StageAge | null | undefined;
  source: string;
  internal: boolean;
  offer_status?: string | null | undefined;
  decision?: { kind: string; value: string; at: string } | null | undefined;
  decision_at?: string | null | undefined;
  withdrawal?: {
    withdrawn_at: string;
    stage: string;
    column?: string | null | undefined;
    reason: string;
    reason_label?: string | null | undefined;
    reason_config_version_id?: string | null | undefined;
  } | null | undefined;
  config_versions: Readonly<Record<string, unknown>>;
  route: string;
  opens: "candidate.open";
}

// ---- his words for stored values ----------------------------------------------------------------------------------

const NONE = "—";

/** His status word for the states his Portfolio counts (On hold, cancelled, filled, archived); none for the others,
 *  whose V1 value has no one-to-one word in his chain (G §22) — the row then carries no state pill. */
const STATE_WORD: Readonly<Record<string, string>> = {
  on_hold: REQUISITION_STATUS.onHold,
  closed_cancelled: REQUISITION_STATUS.cancelled,
  filled: REQUISITION_STATUS.filled,
  closed_filled: REQUISITION_STATUS.filled,
  archived: REQUISITION_STATUS.archived,
};

/** The DC-31 column words of the board, keyed as the server keys its columns. */
export const COLUMN_WORD: Readonly<Record<string, string>> = {
  sourced: G6_COLUMN_LABELS.sourced,
  qualified: G6_COLUMN_LABELS.qualified,
  in_hm_review: G6_COLUMN_LABELS.inHmReview,
  recruiter_screen: G6_COLUMN_LABELS.recruiterScreen,
  hm_interview: G6_COLUMN_LABELS.hmInterview,
  final_interview_loop: G6_COLUMN_LABELS.finalInterviewLoop,
  offer: G6_COLUMN_LABELS.offer,
};

const levelWord = (level: string): string => LEVEL_DISPLAY[level as Level] ?? level;
const num = (value: number | null | undefined): string => (value === null || value === undefined ? NONE : String(value));
const day = (at: string): string => formatDate(at, "UTC");

/** The state a requisition row is in, in his word, or null (no pill). */
export function stateWord(row: Pick<RequisitionRowOut, "status" | "archived">): string | null {
  if (row.archived) return REQUISITION_STATUS.archived;
  return STATE_WORD[row.status] ?? null;
}

function versions(map: Readonly<Record<string, unknown>>): string {
  const values = [...new Set(Object.values(map).flatMap((v) => (typeof v === "string" || typeof v === "number" ? [String(v)] : [])))];
  return values.length ? values.join(" · ") : NONE;
}

function holdsLine(holds: readonly HoldRow[]): ReactNode {
  if (holds.length === 0) return null;
  return holds.map((h, i) => (
    <span key={h.hold_id}>
      {i > 0 ? " · " : null}
      {day(h.placed_at)}
      {" – "}
      {h.resumed_at ? day(h.resumed_at) : null}
      {" "}
      {h.reason_label ?? h.reason}
    </span>
  ));
}

function range(r: RecommendedRangeCell | undefined): string {
  if (!r) return NONE;
  return `${r.currency} ${r.min ?? NONE}–${r.max ?? NONE}`;
}

function HealthCellView({ health }: { health: HealthCell }) {
  if (health.status !== "available" || health.score === null || health.score === undefined || !health.light) return <>{NONE}</>;
  return <Health size="sm" score={health.score} band={health.light} />;
}

// ---- the three tables -------------------------------------------------------------------------------------------------

export interface DrillTableProps {
  data?: ReportDrillOutput | undefined;
  loading?: boolean | undefined;
}

/** The drill-down of one report: whatever `report.drill` returned, in its order. */
export function DrillTable({ data, loading }: DrillTableProps) {
  if (loading || !data) return <Table label={REPORT_PAGE_LABELS.drillDown} loading head={<RequisitionHead withRange={false} />} />;
  if (data.subject === "application") {
    const apps = (data.applications ?? []) as unknown as ApplicationRowOut[];
    const aggregates = data.aggregates ?? [];
    return (
      <>
        {apps.length > 0 || aggregates.length === 0 ? <ApplicationTable rows={apps} /> : null}
        {aggregates.length > 0 ? <AggregateTable rows={aggregates} /> : null}
      </>
    );
  }
  const rows = data.rows as unknown as RequisitionRowOut[];
  return <RequisitionTable rows={rows} />;
}

function RequisitionHead({ withRange }: { withRange: boolean }) {
  return (
    <Tr>
      <Th>{REPORT_PAGE_LABELS.reqId}</Th>
      <Th>{REPORT_PAGE_LABELS.requisition}</Th>
      <Th>{REPORT_DIMENSION_LABELS.level}</Th>
      <Th>{REPORT_DIMENSION_LABELS.department}</Th>
      <Th>{REPORT_DIMENSION_LABELS.brand}</Th>
      <Th>{REPORT_DIMENSION_LABELS.recruiter}</Th>
      <Th>{ROLE_LABELS.hmAbbr}</Th>
      <Th align="r">{REPORT_MEASURE_WORDS.activeDays}</Th>
      <Th align="r">{REPORT_MEASURE_WORDS.daysOpen}</Th>
      <Th align="r">{REPORT_MEASURE_WORDS.holdDays}</Th>
      <Th>{REPORT_MEASURE_WORDS.health}</Th>
      <Th align="r">{METRIC_LABELS.searchReadiness}</Th>
      <Th>{REPORT_PAGE_LABELS.configurationVersion}</Th>
      {withRange ? <Th align="r">{REPORT_PAGE_LABELS.recommendedHiringRange}</Th> : null}
    </Tr>
  );
}

export function RequisitionTable({ rows }: { rows: readonly RequisitionRowOut[] }) {
  const withRange = rows.some((r) => r.recommended_range !== undefined);
  return (
    <Table label={REPORT_PAGE_LABELS.drillDown} dense head={<RequisitionHead withRange={withRange} />} empty={rows.length === 0 ? NONE : undefined}>
      {rows.map((r) => {
        const state = stateWord(r);
        return (
          <Tr key={r.requisition_id} hs={rptHs("row", "requisition")}>
            <Td mono nowrap label={REPORT_PAGE_LABELS.reqId}><TextLink href={r.route}>{r.requisition_id}</TextLink></Td>
            <Td label={REPORT_PAGE_LABELS.requisition}>
              {r.title}
              {state ? <>{" "}<Pill>{state}</Pill></> : null}
            </Td>
            <Td label={REPORT_DIMENSION_LABELS.level}>{levelWord(r.level)}</Td>
            <Td label={REPORT_DIMENSION_LABELS.department}>{r.department}</Td>
            <Td label={REPORT_DIMENSION_LABELS.brand}>{r.brands.length ? r.brands.join(", ") : NONE}</Td>
            <Td label={REPORT_DIMENSION_LABELS.recruiter}>{r.recruiter?.name ?? NONE}</Td>
            <Td label={ROLE_LABELS.hmAbbr}>{r.hiring_manager.name ?? NONE}</Td>
            <Td align="r" label={REPORT_MEASURE_WORDS.activeDays}>{num(r.days_open.active)}</Td>
            <Td align="r" label={REPORT_MEASURE_WORDS.daysOpen}>{num(r.days_open.calendar)}</Td>
            <Td align="r" label={REPORT_MEASURE_WORDS.holdDays} sub={holdsLine(r.holds)}>{num(r.days_open.hold)}</Td>
            <Td label={REPORT_MEASURE_WORDS.health}><HealthCellView health={r.health} /></Td>
            <Td align="r" label={METRIC_LABELS.searchReadiness}>
              {r.readiness && r.readiness.percent !== null && r.readiness.percent !== undefined ? `${r.readiness.percent}%` : NONE}
            </Td>
            <Td mono label={REPORT_PAGE_LABELS.configurationVersion}>{versions(r.config_versions)}</Td>
            {withRange ? <Td align="r" nowrap label={REPORT_PAGE_LABELS.recommendedHiringRange}>{range(r.recommended_range)}</Td> : null}
          </Tr>
        );
      })}
    </Table>
  );
}

export function ApplicationTable({ rows }: { rows: readonly ApplicationRowOut[] }) {
  const head = (
    <Tr>
      <Th>{COMMON_LABELS.candidateId}</Th>
      <Th>{REPORT_PAGE_LABELS.reqId}</Th>
      <Th>{REPORT_PAGE_LABELS.stage}</Th>
      <Th align="r">{REPORT_MEASURE_WORDS.timeInStage}</Th>
      <Th>{REPORT_DIMENSION_LABELS.source}</Th>
      <Th>{REPORT_DIMENSION_LABELS.internal_external}</Th>
      <Th>{REPORT_DIMENSION_LABELS.offer_status}</Th>
      <Th>{REPORT_DIMENSION_LABELS.decision_at}</Th>
      <Th>{REPORT_PAGE_LABELS.withdrawalReason}</Th>
      <Th>{REPORT_PAGE_LABELS.configurationVersion}</Th>
    </Tr>
  );
  return (
    <Table label={REPORT_PAGE_LABELS.drillDown} dense head={head} empty={rows.length === 0 ? NONE : undefined}>
      {rows.map((a) => (
        <Tr key={a.application_id} hs={rptHs("row", "application")}>
          <Td mono nowrap label={COMMON_LABELS.candidateId}><TextLink href={a.route}>{a.candidate_id}</TextLink></Td>
          <Td mono nowrap label={REPORT_PAGE_LABELS.reqId} sub={a.requisition_title}>{a.requisition_id}</Td>
          <Td label={REPORT_PAGE_LABELS.stage}>{a.column ? COLUMN_WORD[a.column] ?? a.column : NONE}</Td>
          <Td
            align="r"
            label={REPORT_MEASURE_WORDS.timeInStage}
            sub={a.in_stage ? <>{SLA_LABELS.calendarTime}{" "}{a.in_stage.calendar}</> : null}
          >
            {a.in_stage ? a.in_stage.active : NONE}
          </Td>
          <Td label={REPORT_DIMENSION_LABELS.source}>{a.source}</Td>
          <Td label={REPORT_DIMENSION_LABELS.internal_external}>
            {a.internal ? REPORT_MEASURE_WORDS.internal : REPORT_MEASURE_WORDS.external}
          </Td>
          <Td label={REPORT_DIMENSION_LABELS.offer_status}>{a.offer_status ?? NONE}</Td>
          <Td nowrap label={REPORT_DIMENSION_LABELS.decision_at}>{a.decision_at ? formatDateTime(a.decision_at) : NONE}</Td>
          <Td label={REPORT_PAGE_LABELS.withdrawalReason} sub={a.withdrawal ? COLUMN_WORD[a.withdrawal.column ?? ""] ?? a.withdrawal.stage : null}>
            {a.withdrawal ? a.withdrawal.reason_label ?? a.withdrawal.reason : NONE}
          </Td>
          <Td mono label={REPORT_PAGE_LABELS.configurationVersion}>{versions(a.config_versions)}</Td>
        </Tr>
      ))}
    </Table>
  );
}

/** The applications the reader may not open, one requisition row each with a count: no candidate key ("an aggregate
 *  must stay identifier-free for HRBP/Interviewer", CR-045). */
export function AggregateTable({ rows }: { rows: readonly AggregateRow[] }) {
  const head = (
    <Tr>
      <Th>{REPORT_PAGE_LABELS.reqId}</Th>
      <Th>{REPORT_PAGE_LABELS.requisition}</Th>
      <Th>{REPORT_DIMENSION_LABELS.level}</Th>
      <Th>{REPORT_DIMENSION_LABELS.department}</Th>
      <Th align="r">{NAV_ITEM_LABELS.candidates}</Th>
    </Tr>
  );
  return (
    <Table label={REPORT_PAGE_LABELS.drillDown} dense head={head}>
      {rows.map((g) => {
        const state = stateWord(g);
        return (
          <Tr key={g.requisition_id} hs={rptHs("row", "aggregate")}>
            <Td mono nowrap label={REPORT_PAGE_LABELS.reqId}><TextLink href={g.route}>{g.requisition_id}</TextLink></Td>
            <Td label={REPORT_PAGE_LABELS.requisition}>
              {g.title}
              {state ? <>{" "}<Pill>{state}</Pill></> : null}
            </Td>
            <Td label={REPORT_DIMENSION_LABELS.level}>{levelWord(g.level)}</Td>
            <Td label={REPORT_DIMENSION_LABELS.department}>{g.department}</Td>
            <Td align="r" label={NAV_ITEM_LABELS.candidates}>{g.count}</Td>
          </Tr>
        );
      })}
    </Table>
  );
}
